import { useRef } from "react";
import { View, Text, StyleSheet, ScrollView, Animated, TouchableOpacity, Image, ImageSourcePropType, Platform, useWindowDimensions } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useNavigation } from "@react-navigation/native";
import { useTranslation } from "react-i18next";
import { spacing, typography } from "../../theme";
import { useColors, ThemeColors } from "../../hooks/useColors";
import { Button } from "../../components/ui/Button";
import { useGuestMode } from "../../contexts/GuestModeContext";
import { AppDownloadBanner } from "../../components/AppDownloadBanner";

// Max width for phone container on web (matches other screens)
const PHONE_CONTAINER_MAX_WIDTH = 430;

// Static landing page images
const LANDING_IMAGES: ImageSourcePropType[] = [
  require("../../assets/landing-1.jpg"), // Black dragon crop top (center)
  require("../../assets/landing-2.jpg"), // Green button-up top (top left)
  require("../../assets/landing-3.jpg"), // Purple lace bralette (bottom right)
  require("../../assets/landing-4.jpg"), // Distressed jeans (middle right)
];

export default function LandingScreen() {
  const { t } = useTranslation();
  const colors = useColors();
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<any>();
  const { setGuestMode } = useGuestMode();
  
  // Get responsive window dimensions
  const windowDimensions = useWindowDimensions();
  
  // On web, constrain width to phone container max width
  const effectiveWidth = Platform.OS === 'web' 
    ? Math.min(windowDimensions.width, PHONE_CONTAINER_MAX_WIDTH)
    : windowDimensions.width;
  
  // Calculate collage height - smaller to fit everything in viewport
  const collageHeight = effectiveWidth * 0.75;

  // Animations - start at final values to prevent dimming issues during hot reload
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const slideAnim = useRef(new Animated.Value(0)).current;

  const styles = createStyles(colors, effectiveWidth, collageHeight);

  const handleGetStarted = () => {
    navigation.navigate("Auth");
  };

  const handleBrowseAsGuest = () => {
    setGuestMode(true);
    navigation.navigate("Main" as never);
  };

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      <ScrollView 
        contentContainerStyle={styles.landingScrollContent}
        showsVerticalScrollIndicator={false}
        bounces={false}
      >
        <AppDownloadBanner 
          compact 
          style={styles.topDownloadBanner}
        />
        <Animated.View style={[styles.collageContainer, { opacity: fadeAnim }]}>
          <View style={styles.logoContainer}>
            <Image source={require("../../assets/logo.png")} style={styles.logo} resizeMode="contain" />
          </View>
          <View style={styles.collageInner}>
            <Animated.View style={[styles.collageImageWrapper, styles.imageCenter, { transform: [{ rotate: '3deg' }, { translateY: slideAnim.interpolate({ inputRange: [0, 20], outputRange: [0, 5] }) }] }]}>
              <Image source={LANDING_IMAGES[0]} style={styles.collageImage} resizeMode="cover" />
            </Animated.View>
            <Animated.View style={[styles.collageImageWrapper, styles.imageTopLeft, { transform: [{ rotate: '-5deg' }, { translateY: slideAnim.interpolate({ inputRange: [0, 20], outputRange: [0, -10] }) }] }]}>
              <Image source={LANDING_IMAGES[3]} style={styles.collageImage} resizeMode="cover" />
            </Animated.View>
            <Animated.View style={[styles.collageImageWrapper, styles.imageBottomRight, { transform: [{ rotate: '-3deg' }, { translateY: slideAnim.interpolate({ inputRange: [0, 20], outputRange: [0, 10] }) }] }]}>
              <Image source={LANDING_IMAGES[1]} style={styles.collageImage} resizeMode="cover" />
            </Animated.View>
            <Animated.View style={[styles.collageImageWrapper, styles.imageMiddleRight, { transform: [{ rotate: '5deg' }, { translateY: slideAnim.interpolate({ inputRange: [0, 20], outputRange: [0, -5] }) }] }]}>
              <Image source={LANDING_IMAGES[2]} style={styles.collageImage} resizeMode="cover" />
            </Animated.View>
          </View>

          <View style={styles.collageTextContent}>
            <Text style={styles.headline}>{t("auth.landingHeadline")}</Text>
            <Text style={styles.description}>{t("auth.landingDescription")}</Text>
          </View>
        </Animated.View>

        <Animated.View style={[styles.landingButtonContainer, { opacity: fadeAnim, transform: [{ translateY: slideAnim }] }]}>
          <Button 
            onPress={handleGetStarted} 
            variant="primary" 
            style={styles.mainButton}
            textStyle={styles.mainButtonText}
          >
            Get Started
          </Button>
          <TouchableOpacity onPress={handleBrowseAsGuest} style={styles.skipLink}>
            <Text style={styles.skipLinkText}>Browse as Guest</Text>
          </TouchableOpacity>
        </Animated.View>
      </ScrollView>
    </View>
  );
}

const createStyles = (colors: ThemeColors, width: number, collageHeight: number) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    landingScrollContent: {
      flexGrow: 1,
      backgroundColor: colors.background,
      alignItems: 'center',
    },
    collageContainer: {
      width: '100%',
      maxWidth: PHONE_CONTAINER_MAX_WIDTH,
      justifyContent: 'center',
      alignItems: 'center',
      paddingTop: spacing.xs,
    },
    logoContainer: {
      marginBottom: spacing.xs,
      alignItems: 'center',
      justifyContent: 'center',
    },
    logo: {
      width: 60,
      height: 60,
      borderRadius: 14,
      overflow: 'hidden',
    },
    collageInner: {
      width: width,
      height: collageHeight,
      position: 'relative',
    },
    collageImageWrapper: {
      position: 'absolute',
      borderRadius: 12,
      overflow: 'hidden',
      backgroundColor: colors.backgroundSecondary,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.2,
      shadowRadius: 8,
      elevation: 8,
      borderWidth: 2,
      borderColor: colors.white,
    },
    collageImage: {
      width: '100%',
      height: '100%',
    },
    // Center image (black crop top) - largest, front-center
    imageCenter: {
      width: width * 0.42,
      height: width * 0.52,
      top: '15%',
      left: width * 0.29,
      zIndex: 10,
    },
    // Top left image (jeans) - behind others, top left
    imageTopLeft: {
      width: width * 0.32,
      height: width * 0.4,
      top: '2%',
      left: spacing.lg,
      zIndex: 5,
    },
    // Bottom right image (green top) - front-right, overlapping
    imageBottomRight: {
      width: width * 0.36,
      height: width * 0.46,
      bottom: spacing.xs,
      right: spacing.lg,
      zIndex: 15,
    },
    // Middle right image (bralette) - small, behind bottom right
    imageMiddleRight: {
      width: width * 0.28,
      height: width * 0.34,
      top: '5%',
      right: spacing.md,
      zIndex: 2,
    },
    collageTextContent: {
      marginTop: spacing.sm,
      paddingHorizontal: spacing.lg,
      alignItems: 'center',
    },
    headline: {
      ...typography.title,
      fontSize: 24,
      textAlign: 'center',
      color: colors.text,
      lineHeight: 30,
      marginBottom: spacing.xs,
      fontWeight: '900',
    },
    description: {
      ...typography.body,
      fontSize: 13,
      textAlign: 'center',
      color: colors.textSecondary,
      lineHeight: 18,
      fontWeight: '500',
    },
    landingButtonContainer: {
      paddingHorizontal: spacing.lg,
      marginTop: spacing.sm,
      paddingBottom: spacing.md,
      gap: spacing.xs,
      alignItems: 'center',
      width: '100%',
      maxWidth: PHONE_CONTAINER_MAX_WIDTH,
    },
    mainButton: {
      height: 48,
      width: '100%',
      borderRadius: 14,
      backgroundColor: colors.primary,
    },
    mainButtonText: {
      fontSize: 16,
      fontWeight: '700',
      color: '#000000',
    },
    skipLink: {
      marginTop: spacing.sm,
      paddingBottom: spacing.sm,
    },
    skipLinkText: {
      fontSize: 13,
      fontWeight: '600',
      color: colors.textSecondary,
    },
    topDownloadBanner: {
      width: '100%',
      maxWidth: PHONE_CONTAINER_MAX_WIDTH,
      marginTop: spacing.sm,
      paddingHorizontal: spacing.md,
    },
  });
